import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import localeFr from '@angular/common/locales/fr';
import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, LOCALE_ID, NgModule, inject, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouteReuseStrategy } from '@angular/router';
import { LeafletModule } from '@bluehalo/ngx-leaflet';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular/lazy';
import { IonicStorageModule } from '@ionic/storage-angular';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideNgxWebstorage, withLocalStorage, withNgxWebstorageConfig, withSessionStorage } from 'ngx-webstorage';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ApiHeaderInjectorInterceptor } from './interceptors/api-header-injector-interceptor.service';
import { AuthInterceptor } from './interceptors/auth.interceptor';
import { UnauthorizedInterceptor } from './interceptors/unauthorized.interceptor';
import { MenuModule } from './menu/menu.module';
import { JhiEventManagerImpl } from './services/event-manager.impl';
import { JhiEventManager } from './services/event-manager.service';
import { OfflineNotificationService } from './services/offline-notification.service';
import { StorageMigrationService } from './services/storage-migration.service';
import { UserEventWriteService } from './services/user-event.write.service';
import { SharedModule } from './shared/shared.module';

registerLocaleData(localeFr);

export function userEventWriteServiceInit(provider: UserEventWriteService) {
  return () => provider.demarrer();
}

export function storageMigrationInit(storageMigrationService: StorageMigrationService) {
  return async () => {
    // Migration du storage pour gérer :
    // 1. Le changement d'origine Android (https://localhost -> http://localhost)
    // 2. Le breaking change du format des clés ngx-webstorage
    // Doit être exécuté avant le bootstrap de l'application.
    await storageMigrationService.migrate();
  };
}

@NgModule({
  declarations: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    IonicModule.forRoot({ mode: 'md' }),
    IonicStorageModule.forRoot(),
    AppRoutingModule,
    MenuModule,
    FontAwesomeModule,
    SharedModule,
    LeafletModule,
  ],
  providers: [
    provideTranslateService({
      fallbackLang: 'fr',
      loader: provideTranslateHttpLoader({ prefix: 'assets/i18n/', suffix: '.json' }),
    }),
    OfflineNotificationService,
    UserEventWriteService,
    // A supprimer si tous les utilisateurs sont sur une version 1.18.3 ou supérieur
    StorageMigrationService,
    {
      provide: APP_INITIALIZER,
      useFactory: storageMigrationInit,
      deps: [StorageMigrationService],
      multi: true,
    },
    provideAppInitializer(() => {
      const initializerFn = userEventWriteServiceInit(inject(UserEventWriteService));
      return initializerFn();
    }),
    ScreenOrientation,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiHeaderInjectorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: UnauthorizedInterceptor,
      multi: true,
    },
    {
      provide: JhiEventManager,
      useClass: JhiEventManagerImpl,
    },
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    { provide: LOCALE_ID, useValue: 'fr-FR' },
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    provideNgxWebstorage(withNgxWebstorageConfig({ separator: ':', caseSensitive: true }), withLocalStorage(), withSessionStorage()),
  ],
})
export class AppModule {}
